/* Native capabilities when the driver app runs as an installed app
 * (Capacitor), with graceful web fallbacks so the exact same build still
 * works in a browser and as a PWA.
 *
 * This matters beyond convenience: Apple rejects apps that are only a
 * wrapper around a website (App Store Review Guideline 4.2), so the packaged
 * build must use real device capability - here the camera and GPS.
 */

export function isNative() {
  try {
    // Capacitor injects this global into the WebView.
    return !!(window.Capacitor && window.Capacitor.isNativePlatform
      && window.Capacitor.isNativePlatform());
  } catch {
    return false;
  }
}

export function platform() {
  try {
    return (window.Capacitor && window.Capacitor.getPlatform)
      ? window.Capacitor.getPlatform() : "web";
  } catch {
    return "web";
  }
}

/** Base URL for API calls. In a packaged app the page is served from
 *  https://localhost by the WebView, so a relative "/api" would never reach
 *  the server - it has to be absolute. */
export function apiBaseUrl() {
  const configured = process.env.REACT_APP_API_BASE;
  if (configured) return configured;
  if (isNative()) return "https://www.davtransport.nz/api/v1";
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://127.0.0.1:8080/v1";
  return "/api/v1";
}

/**
 * Take a photo. Returns a Blob, or null if the user cancelled.
 * Uses the device camera through Capacitor when installed; the caller falls
 * back to a file input on the web.
 */
export async function takePhoto() {
  if (!isNative()) return null;
  const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
  const photo = await Camera.getPhoto({
    quality: 70,
    // Resize on the device: originals are 3-12MB and drivers on rural
    // connections abandon those uploads.
    width: 1600,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    source: CameraSource.Camera,
    saveToGallery: false,
    correctOrientation: true,
  });
  if (!photo?.webPath) return null;
  const res = await fetch(photo.webPath);
  return await res.blob();
}

/** Current position, or {} when unavailable. Never rejects - a delivery must
 *  not be blocked because GPS is slow or denied. */
export async function getPosition(timeoutMs = 4000) {
  try {
    if (isNative()) {
      const { Geolocation } = await import("@capacitor/geolocation");
      const perm = await Geolocation.checkPermissions();
      if (perm.location === "denied") return {};
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true, timeout: timeoutMs,
      });
      return { lat: pos.coords.latitude, lon: pos.coords.longitude };
    }
    if (!navigator.geolocation) return {};
    return await new Promise((resolve) => {
      const timer = setTimeout(() => resolve({}), timeoutMs);
      navigator.geolocation.getCurrentPosition(
        (p) => { clearTimeout(timer); resolve({ lat: p.coords.latitude, lon: p.coords.longitude }); },
        () => { clearTimeout(timer); resolve({}); },
        { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30000 }
      );
    });
  } catch {
    return {};
  }
}

/** Subscribe to connectivity changes. Returns an unsubscribe function. */
export function onNetworkChange(handler) {
  let remove = null;
  if (isNative()) {
    import("@capacitor/network").then(({ Network }) => {
      Network.addListener("networkStatusChange", (s) => handler(s.connected))
        .then((h) => { remove = () => h.remove(); });
    }).catch(() => undefined);
  }
  const on = () => handler(true);
  const off = () => handler(false);
  window.addEventListener("online", on);
  window.addEventListener("offline", off);
  return () => {
    window.removeEventListener("online", on);
    window.removeEventListener("offline", off);
    if (remove) remove();
  };
}

/** Match the app chrome to the dark UI once the shell is up. */
export async function initNativeChrome() {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark });
    if (platform() === "android") {
      await StatusBar.setBackgroundColor({ color: "#0f172a" });
    }
  } catch {
    /* status bar styling is cosmetic */
  }
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide();
  } catch {
    /* splash auto-hides anyway */
  }
}
